import { messageFromApiErrorBody } from "@/services/api";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

export class PublicApiError extends Error {
  readonly status: number;
  readonly error: string;
  readonly detail: string;

  constructor(status: number, body: unknown, fallback: string) {
    const detail = messageFromApiErrorBody(body, fallback);
    const code =
      body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string"
        ? (body as { error: string }).error
        : "";
    super(detail);
    this.name = "PublicApiError";
    this.status = status;
    this.error = code;
    this.detail = detail;
  }
}

function mergeHeaders(init: HeadersInit | undefined, forceJson: boolean): Record<string, string> {
  const headers: Record<string, string> = {};
  if (forceJson) {
    headers["Content-Type"] = "application/json";
  }
  new Headers(init).forEach((value, key) => {
    headers[key] = value;
  });
  return headers;
}

async function publicRequest<T>(
  path: string,
  init: RequestInit | undefined,
  forceJson: boolean
): Promise<T> {
  const { getCsrfToken } = await import("@/utils/csrf");
  const headers = mergeHeaders(init?.headers, forceJson);
  const method = (init?.method || "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRFToken"] = csrf;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (res.status === 204 || res.status === 205) {
    if (!res.ok) {
      throw new PublicApiError(res.status, {}, `Request failed (${res.status})`);
    }
    return undefined as T;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new PublicApiError(res.status, data, `Request failed (${res.status})`);
  }
  return data as T;
}

/** Shared unauthenticated fetch that surfaces real API validation messages. */
export async function publicApiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return publicRequest<T>(path, init, true);
}

/** Same as `publicApiFetch`, but does not set `Content-Type` (browser sets the multipart boundary). */
export async function publicApiFetchMultipart<T>(path: string, init?: RequestInit): Promise<T> {
  return publicRequest<T>(path, init, false);
}
